import React from 'react';
import { View, StyleSheet, StyleProp, ViewStyle, TextStyle, TouchableOpacity } from 'react-native';
import MyAppText from './MyAppText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

export type ModeType = 'SQUARE' | 'ROUNDED';

export type BadgeColor = 'transparencyBlack' | 'blue' | 'red' | 'orange' | 'green' | 'purple' | 'gray' | 'yellow' | 'teal';

// Define the types for the Badge component's props
type BadgeProps = {
    /**
     * The content to display inside the badge. Can be a number or a string.
     */
    children: React.ReactNode;
    /**
     * The size of the badge. 'small' for compact, 'medium' for standard, 'large' for more prominent.
     * Defaults to 'medium'.
     */
    size?: 'small' | 'medium' | 'large';
    /**
     * Optional style to apply to the badge container (View).
     */
    style?: StyleProp<ViewStyle>;
    /**
     * Optional style to apply to the badge text (Text).
     */
    textStyle?: StyleProp<TextStyle>;
    removable?: boolean;
    onRemove?: () => void;
    mode?: ModeType;
    count?: number;
    color?: BadgeColor;
} & ({
    mode: 'SQUARE';
    /**
     * The background color of the badge. Defaults to a standard primary blue.
     */
    backgroundColor?: undefined;
    /**
     * The color of the text inside the badge. Defaults to white.
     */
    textColor?: undefined;
} | {
    mode: 'ROUNDED';
    /**
     * The background color of the badge. Defaults to a standard primary blue.
     */
    backgroundColor?: string;
    /**
     * The color of the text inside the badge. Defaults to white.
     */
    textColor?: string;
});

const Badge: React.FC<BadgeProps> = ({
    children,
    backgroundColor = '#2196F3',
    color,
    textColor = '#FFFFFF',
    size = 'medium',
    style,
    textStyle,
    removable,
    onRemove,
    mode,
    count,
}) => {
    const { styles, theme } = useStyles(styleSheet);

    // Determine size styles based on the 'size' prop
    const sizeStyles = StyleSheet.create({
        small: {
            minWidth: 18,
            minHeight: 22,
            borderRadius: mode === 'ROUNDED' ? 24 : 4,
            paddingHorizontal: 8,
            paddingVertical: 4,
        },
        medium: {
            minWidth: 24,
            minHeight: 24,
            borderRadius: mode === 'ROUNDED' ? 24 : 4,
            paddingHorizontal: 8,
            paddingVertical: 4,
        },
        large: {
            minWidth: 32,
            minHeight: 27,
            borderRadius: mode === 'ROUNDED' ? 24 : 4,
            paddingHorizontal: 8,
            paddingVertical: 4,
        },
    });

    const fontSizeStyles = StyleSheet.create({
        small: {
            fontSize: 10,
        },
        medium: {
            fontSize: 12,
        },
        large: {
            fontSize: 14,
        },
    });

    return (
        <View
            style={[
                styles.badgeContainer(color ? theme.colors.badges[color].backgroundColor : backgroundColor),
                sizeStyles[size],
                { backgroundColor: color ? theme.colors.badges[color].backgroundColor : backgroundColor },
                style, // Allow external style to override or extend
            ]}
        >
            <MyAppText
                style={[
                    styles.badgeText,
                    fontSizeStyles[size],
                    { color: color ? theme.colors.badges[color].textColor : textColor },
                    textStyle, // Allow external textStyle to override or extend
                ]}
            >
                {children}
            </MyAppText>
            {removable &&
                <TouchableOpacity onPress={onRemove}>
                    <FontAwesomeIcon icon={faClose} fontSize={fontSizeStyles[size].fontSize} color={color ? theme.colors.badges[color].textColor : textColor} />
                </TouchableOpacity>}

            {count && count > 1 &&
                <View style={{
                    backgroundColor: color ? theme.colors.badges[color].textColor : textColor,
                    borderRadius: 24,
                    width: sizeStyles[size].minHeight - 6,
                    height: sizeStyles[size].minHeight - 6,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}>
                    <MyAppText style={{
                        color: color ? theme.colors.badges[color].backgroundColor : backgroundColor,
                        fontSize: fontSizeStyles[size].fontSize,
                    }}>{count}</MyAppText>
                </View>
            }
        </View>
    );
};

const styleSheet = createStyleSheet((theme) => ({
    badgeContainer: (bgColor: string) => ({
        flexDirection: 'row',
        columnGap: 8,
        justifyContent: 'center',
        alignItems: 'center',
        alignSelf: 'flex-start', // Ensures the badge only takes up necessary width/height
        borderWidth: bgColor.toLocaleLowerCase() == '#ffffff' ? 1 : 0,
        borderColor: theme.colors.neutral['100'],
    }),
    badgeText: {
        fontWeight: '600',
        textAlign: 'left',
    },
}));

export default Badge;
